// @flow

import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import API from 'services/API';
import strings from 'strings';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import NameForm from 'UI/components/organisms/NameForm';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { getErrorMessage, getFeatureFlags, hasFeatureFlag } from 'UI/utils';
import { preloadFromBackend, prepareFormToSubmit } from 'UI/utils/forms';

import { FormFieldsMap } from '../NameForm/fields';
import { formatPhoneNumbers, PHONE_FORMAT_MODES } from '../PhoneNumbersForm/utils';

type NameEditProps = {
  name: any,
  onEditCompleted: (profile: any) => void,
  onEditClosed: () => void
};

const NameEdit = ({ name, onEditCompleted, onEditClosed, FPDrawerProps }: NameEditProps) => {
  const dispatch = useDispatch();

  const defaultValues = preloadFromBackend(name, FormFieldsMap);

  const hasMultiplePhones =
    defaultValues.phones.length > 0 &&
    getFeatureFlags().includes(FeatureFlags.ContactsMultiplePhones);

  const [uiState, setUiState] = useState({
    isSaving: false,
    isSuccess: false,
    isFormDisabled: false,
    isReadOnly: false
  });

  const form = useForm(name?.id ? { defaultValues } : {});

  const isMultiplePhonesEnabled = hasFeatureFlag(FeatureFlags.ContactsMultiplePhones);

  const onSubmit = async formData => {
    try {
      setUiState(prevState => ({ ...prevState, isSaving: true }));
      const preparedNameData = prepareFormToSubmit(formData, FormFieldsMap);
      const preparedPhones = isMultiplePhonesEnabled
        ? formatPhoneNumbers(formData, PHONE_FORMAT_MODES.submit)
        : null;
      const dataToSend = {
        ...preparedNameData,
        phones: preparedPhones
      };
      const response = await API.put(`${Endpoints.Names}/${name.id}`, dataToSend, {
        apiVersion: 2
      });
      if (response.data && response.status === HTTPStatusCodes.Ok) {
        dispatch(
          showAlert({
            severity: 'success',
            title: 'Awesome',
            body: 'The contact was updated successfully'
          })
        );

        onEditCompleted();
      }
    } catch (err) {
      dispatch(
        showAlert({
          severity: 'error',
          title: 'Contact',
          body: getErrorMessage(err)
        })
      );
    }
    setUiState(prevState => ({
      ...prevState,
      isSaving: false,
      isSuccess: false
    }));
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onEditClosed
      },
      GridItemProps: {
        md: 3
      }
    },
    {
      props: {
        initialText: strings.shared.ui.save,
        variant: 'contained',
        type: 'submit',
        disabled: uiState.isSaving,
        onProgressText: strings.shared.ui.saving,
        isSaving: uiState.isSaving,
        onClick: form.handleSubmit(onSubmit)
      },
      GridItemProps: {
        md: 3
      },
      mode: 'save'
    }
  ];

  return (
    <FPDrawer
      FPDrawerProps={FPDrawerProps}
      CardHeaderProps={{
        title: strings.inventoryProfiles.common.editProfile,
        subheader: name?.personalInformation?.full_name
      }}
      onClose={onEditClosed}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <FormContext {...form}>
        <NameForm isEditing hasMultiplePhones={hasMultiplePhones} />
      </FormContext>
    </FPDrawer>
  );
};

export default NameEdit;
